import React from "react";
import { useParams } from "react-router-dom";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { Skeleton } from "../../../../atoms/skeleton";
import { formattedDateTime, pascalToSnakeCase } from "../../../../../utils";
import { Divider } from "../../../../atoms/divider";
import { Table } from "../../../../atoms/table";
import { useTableHooks } from "../../../../atoms/table/useTableHooks";
import { Input } from "../../../../atoms/input";
import { usePaginationHook } from "../../../../molecules/pagination/usePaginationHook";
import { Pagination } from "../../../../molecules/pagination";
import { Dropdown } from "../../../../atoms/dropdown";
import { PAGE_NAME } from "../../../../../constants/constants";
import { useGetSleetProjectMembersQuery, useGetSleetProjectQuery } from "../../../../../store/credentialsManagement/credentialsSlice";
import { Icon } from "../../../../atoms/icon";
import { toast } from "react-toastify";

export const SleetProjectDetails = () => {
  const pageClassName = "p-sleetProjectDetails";
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useGetSleetProjectQuery(id as string);

  const {
    search,
    query,
    pageChangeHandler,
    sortHandler,
    limitChangeHandler,
    searchHandler,
  } = useTableHooks();

  const {
    totalData,
    handleFirstPage,
    handlePrevPage,
    handleLastPage,
    handleNextPage,
    renderPageNumbers,
  } = usePaginationHook({
    totalNumberOfData: data?.result?.total,
    perPage: query.limit,
    setCurrentPage: pageChangeHandler,
    currentPage: query.current,
    className: "m-pagination",
  });

  const { data: membersData, isLoading: membersIsLoading } =
    useGetSleetProjectMembersQuery({
      id,
      search,
      current_page: query.current,
      limit: query.limit,
      sort_by: pascalToSnakeCase(query.sortBy),
      sort_direction: query.sortDirection,
    });
  const appsTableHeader = [
    {
      title: "Serial",
      dataIndex: "id",
      isSortable: false,
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      isSortable: true,
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      isSortable: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      isSortable: true,
    }
  ];

  const sortFormatHandler = (sortBy: string) => {
    switch (sortBy) {
      case "memberFirstName":
        sortBy = "first_name";
        break;
      case "memberLastName":
        sortBy = "last_name";
        break;
      case "memberEmail":
        sortBy = "email";
        break;
      default:
        break;
    }

    sortHandler(sortBy);
  };

  return (
    <div className={pageClassName}>
      <Breadcrumb />
      <p className={`${pageClassName}__title`}>{PAGE_NAME.MIRO_BOARD_INFO}</p>
      <div className={`${pageClassName}__container`}>
        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>Name</p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? <Skeleton height="small" /> : data?.result?.name}
          </p>
        </div>

        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Secret Name
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : (
              data?.result?.secretName ?? 0
            )}
          </p>
        </div>

        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Production Feed URL
          </p>
          {isLoading ? (
            <Skeleton height="small" />
          ) : (
            <div className={`${pageClassName}__container__url`}>
              <p className={`${pageClassName}__container__url__value`}>
                {data?.result?.productionFeedUrl}
              </p>
              <button
                className={`${pageClassName}__container__url__button`}
                onClick={() => {
                  navigator.clipboard.writeText(
                    data?.result?.productionFeedUrl
                  );
                  toast.success("URL copied to clipboard");
                }}
                type="button"
              >
                <Icon icon="copy" size="secondary" />
              </button>
            </div>
          )}
        </div>

        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Development Feed URL
          </p>
          {isLoading ? (
            <Skeleton height="small" />
          ) : (
            <div className={`${pageClassName}__container__url`}>
              <p className={`${pageClassName}__container__url__value`}>
                {data?.result?.developmentFeedUrl}
              </p>
              <button
                className={`${pageClassName}__container__url__button`}
                onClick={() => {
                  navigator.clipboard.writeText(
                    data?.result?.developmentFeedUrl
                  );
                  toast.success("URL copied to clipboard");
                }}
                type="button"
              >
                <Icon icon="copy" size="secondary" />
              </button>
            </div>
          )}
        </div>

        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Requested By
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : (
              data?.result?.createdBy
                ? data.result.createdBy.firstName + " " + data.result.createdBy.lastName
                : "-"
            )}
          </p>
        </div>

        <div className={`${pageClassName}__container__item`}>
          <p className={`${pageClassName}__container__item__title`}>
            Created At
          </p>
          <p className={`${pageClassName}__container__item__value`}>
            {isLoading ? (
              <Skeleton height="small" />
            ) : data?.result?.createdAt ? (
              formattedDateTime(data?.result?.createdAt)
            ) : (
              "-"
            )}
          </p>
        </div>

        {!membersIsLoading && (
          <>
            <Divider />
            <div className={`${pageClassName}__apps`}>
              <p className={`${pageClassName}__apps__title`}>Members</p>
              <div className={`${pageClassName}__apps__container`}>
                <div className={`${pageClassName}__search`}>
                  <Input
                    placeholder="Search"
                    onChange={(e) => {
                      searchHandler(e);
                      handleFirstPage();
                    }}
                    value={search}
                  />
                </div>
                <Table
                  header={appsTableHeader}
                  data={membersData?.result?.data ?? []}
                  loading={membersIsLoading}
                  firstLoad={membersIsLoading}
                  sortHandler={sortFormatHandler}
                  startIndex={membersData?.result?.from}
                />
                <div className={`${pageClassName}__pagination`}>
                  <div className={`${pageClassName}__pagination__limit`}>
                    <p>Display</p>
                    <div className={`${pageClassName}__pagination__dropdown`}>
                      <Dropdown
                        options={[
                          { label: "20", value: 20 },
                          { label: "50", value: 50 },
                          { label: "100", value: 100 },
                        ]}
                        selected={{ label: query.limit, value: query.limit }}
                        onClickChange={(e) => {
                          limitChangeHandler(e.value as number);
                          handleFirstPage();
                        }}
                        isBordered
                        isIcon

                      />
                    </div>
                  </div>
                  <div className={`${pageClassName}__pagination__number`}>
                    {membersData?.result?.total ? (
                      <p>
                        Show {membersData?.result?.from} -{" "}
                        {membersData?.result?.from + query.limit - 1 <
                          membersData?.result?.total
                          ? membersData?.result?.from + query.limit - 1
                          : membersData?.result?.total}{" "}
                        of {membersData?.result?.total}
                      </p>
                    ) : (
                      <p>Show 00 - 00 of 00</p>
                    )}
                  </div>

                  <div className={`${pageClassName}__pagination__nav`}>
                    {membersData?.result?.total > query.limit && (
                      <Pagination
                        totalData={totalData}
                        page={query.current}
                        handleFirstPage={handleFirstPage}
                        handlePrevPage={handlePrevPage}
                        handleLastPage={handleLastPage}
                        handleNextPage={handleNextPage}
                        renderPageNumbers={renderPageNumbers}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
